import moment from 'moment';
import Fuse from 'fuse.js';
import _ from 'lodash';
import { LinkCopied, Badge } from './repositories-modal.components';
import { Tooltip } from '@material-ui/core';
import copy from 'copy-to-clipboard';
import { LINWEB_URI } from 'common/constants/api-urls';
import {
  SESSION_STORAGE_KEYS,
  TRACKING,
  MONTH_AND_YEAR_FORMAT,
  DATE_FORMAT_WITH_FULL_DAY,
  MONTHS_PARAM
} from 'common/constants/constants';
import * as ZendeskService from 'common/services/zendesk.service';
import { getColor } from 'common/constants/_colors';
import Text from 'components/base/Text';
import { Flex } from 'components/base/layout-components';
import * as TrackingService from 'common/services/tracking.service';
import { getFetchRepositories } from 'common/services/repositories.service';
import Checkbox from 'components/base/Checkbox';
import { SPACES } from 'common/constants/sizes';
import Icon from 'components/base/Icon';

const INACTIVE_REPO_THRESHOLD_IN_MONTHS = 3;
const USER_OWNER_TYPE = 'User';

export const isInactiveRepository = updatedAt =>
  updatedAt &&
  moment().diff(moment(updatedAt), MONTHS_PARAM) >=
    INACTIVE_REPO_THRESHOLD_IN_MONTHS;

export const getActiveRepositories = repositories =>
  repositories
    ? repositories.filter(({ updated_at }) => !isInactiveRepository(updated_at))
    : [];

const isOrgLevelRepo = ({ owner_type }) => owner_type !== USER_OWNER_TYPE;

export const getOrgLevelRepos = repositories =>
  repositories.filter(isOrgLevelRepo);

const fuseOptions = {
  shouldSort: true,
  threshold: 0.2,
  location: 0,
  distance: 100,
  maxPatternLength: 32,
  keys: ['name', 'http_url']
};

export const getFilteredRepositories = (repositories, term = '') =>
  term ? new Fuse(repositories, fuseOptions).search(term) : repositories;

export const findRelevantSensorAuthorization = (
  gitIntegrations,
  sensor_authorization_id
) =>
  sensor_authorization_id
    ? gitIntegrations.find(({ id }) => sensor_authorization_id === id)
    : _.get(gitIntegrations, '[0]');

export const shouldShowNoReposTemplate = (activeRepos = [], isOnboarding) =>
  !activeRepos.length ||
  (isOnboarding && !getOrgLevelRepos(activeRepos).length);

export const inviteYourAdmin = (toggleSnackBar, { id: accountId }) => {
  copy(`${LINWEB_URI}?${SESSION_STORAGE_KEYS.REF_ACCOUNT_ID}=${accountId}`);

  toggleSnackBar({
    message: <LinkCopied />,
    variant: 'success'
  });
};

export const talkToUs = () => {
  TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
    page: TRACKING.PAGES.ONBOARDING,
    page_tab: TRACKING.PAGE_TABS.SELECT_REPOS,
    click_type: TRACKING.CLICK.TALK_TO_US
  });
  ZendeskService.openSupportRequest();
};

export const createdColumn = ({
  isNew,
  created_at,
  fontColor,
  iconColor,
  number
}) => {
  const createdText = (
    <Text
      size='small'
      color={fontColor}
      pl={SPACES['x-small']}
      whiteSpace='nowrap'
    >
      {isNew
        ? `${number ? number + ' ' : ''}NEW! added ${moment(created_at).format(
            DATE_FORMAT_WITH_FULL_DAY
          )}`
        : moment(created_at).format(MONTH_AND_YEAR_FORMAT)}
    </Text>
  );

  return isNew ? (
    <>
      <Icon name='fibermanualrecordicon' color={iconColor} size='small-shape' />
      {createdText}
    </>
  ) : (
    createdText
  );
};

export const getRepoModalTableColumns = ({ numOfActiveRepositories }) => {
  return [
    {
      ColumnHeaderComponent: () => (
        <Flex
          height='100%'
          width='100%'
          justify='center'
          items='center'
          content='center'
        />
      )
    },
    {
      name: 'REPOSITORIES',
      dataPoint: 'name'
    },
    {
      name: 'PATH',
      dataPoint: 'path'
    },
    {
      name: 'ACTIVE',
      dataPoint: 'active',
      ColumnHeaderComponent: () => (
        <Text textTransform='uppercase' color='gray' whiteSpace='nowrap'>
          {numOfActiveRepositories} ACTIVE REPOS
        </Text>
      )
    },
    {
      name: 'CREATED',
      dataPoint: 'created'
    }
  ];
};

export const getRepoModalTableData = ({
  providerConfig = {},
  selected,
  filteredRepositories,
  isOnboarding,
  isCheckboxDisabled,
  toggleRepositorySelection,
  numOfSelectableRepos
}) =>
  _.chain(filteredRepositories)
    .orderBy(['isNew', 'updated_at', 'owner_type'], ['desc', 'desc', 'asc'])
    .map(
      ({
        id,
        name,
        http_url,
        owner_type = 'Organization',
        updated_at = null,
        created_at = null,
        isNew
      }) => {
        const isInactive = isOnboarding
          ? isInactiveRepository(updated_at) || !isOrgLevelRepo({ owner_type })
          : isInactiveRepository(updated_at);
        const inactiveFontColor = 'silverSand';
        const fontColor = isInactive ? inactiveFontColor : 'osloGray';
        const iconColor = isInactive ? inactiveFontColor : 'dodgerBlue';

        return {
          select: (
            <Tooltip
              interactive
              placement='top-start'
              disableHoverListener={
                selected.has(id) || (!!numOfSelectableRepos && !isInactive)
              }
              title={
                isInactiveRepository(updated_at) ? (
                  <>
                    <Text size='small' pb='0.3rem'>
                      This repository has not been active in the last 3 months.
                    </Text>
                    <Text size='small'>Scanning it will show no results</Text>
                  </>
                ) : !numOfSelectableRepos ? (
                  <Text>You have reached your limit of repositories</Text>
                ) : (
                  <Text>
                    Select team repos to proceed. You will be able to add
                    personal repos after onboarding
                  </Text>
                )
              }
            >
              <Flex justify='center' items='center' content='center'>
                <Checkbox
                  data-cy='repository'
                  type='checkbox'
                  disabled={isCheckboxDisabled(id) || isInactive}
                  onClick={() => {
                    toggleRepositorySelection(id);
                  }}
                  checked={selected.has(id)}
                />
              </Flex>
            </Tooltip>
          ),
          name: (
            <Flex items='center'>
              <Text
                size='medium'
                color={isInactive ? inactiveFontColor : 'shark'}
                ellipsisAfter='14rem'
              >
                {name}
              </Text>
            </Flex>
          ),
          path: (
            <Flex items='center'>
              <Text size='small' color={fontColor}>
                {_.get(
                  providerConfig.formatHttpUrl(http_url),
                  'displayPath',
                  ''
                )}
              </Text>
            </Flex>
          ),
          active: (
            <Flex items='center'>
              {isInactiveRepository(updated_at) ? (
                <Badge type='inactive'>NOT ACTIVE</Badge>
              ) : (
                <Badge type='active'>
                  <div
                    style={{
                      display: 'inline-block',
                      marginRight: '0.5rem',
                      width: '0.6rem',
                      height: '0.6rem',
                      borderRadius: '50%',
                      backgroundColor: getColor('pastelGreen')
                    }}
                  />
                  ACTIVE
                </Badge>
              )}
            </Flex>
          ),
          ...(isInactive
            ? {
                styles: {
                  color: getColor('silverSand'),
                  backgroundColor: getColor('catskillGray')
                }
              }
            : {}),
          created: (
            <Flex items='center'>
              {created_at
                ? createdColumn({ isNew, created_at, fontColor, iconColor })
                : ''}
            </Flex>
          )
        };
      }
    )
    .value();

export const getRepositoriesFromProvider = async ({
  sensor_authorization_id,
  repositories,
  isOnboarding,
  providerConfig
}) => {
  try {
    const providerRepositories = await getFetchRepositories({
      sensor_authorization_id,
      providerConfig
    });

    const selectableRepositories = _.differenceBy(
      providerRepositories,
      repositories,
      'http_url'
    );

    const activeRepos = getActiveRepositories(repositories);
    const orgLevelRepos = getOrgLevelRepos(repositories);
    const activeOrgLevelRepos = getOrgLevelRepos(activeRepos);
    TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
      page: isOnboarding ? TRACKING.PAGES.ONBOARDING : TRACKING.PAGES.SETTINGS,
      page_tab: TRACKING.PAGE_TABS.SELECT_REPOS,
      context: {
        nTotalRepos: repositories.length,
        nActiveRepos: activeRepos.length,
        nOrgLevelRepos: orgLevelRepos.length,
        nActiveOrgLevelRepos: activeOrgLevelRepos.length
      }
    });

    return {
      items: selectableRepositories || []
    };
  } catch (e) {
    return {
      error: _.get(e, 'response.status', 500),
      items: []
    };
  }
};

export const findAndDeleteSensor = ({
  isOnboarding,
  gitIntegrations,
  sensor_authorization_id,
  deleteSensorAuthorization
}) => {
  if (isOnboarding) {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.ONBOARDING,
      page_tab: TRACKING.PAGE_TABS.SELECT_REPOS,
      click_type: TRACKING.CLICK.CHANGE_AUTHORIZATION
    });
  }
  const sensor = findRelevantSensorAuthorization(
    gitIntegrations,
    sensor_authorization_id
  );
  if (sensor) {
    deleteSensorAuthorization(sensor.id);
  }
};
